import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/common';
import { Button, Input } from '../components/ui';
import { CONTACT_INFO } from '../constants';
import './MyBooking.css';

interface BookingResult {
	id: string;
	name: string;
	date: string;
	time: string;
	guestCount: number;
	region: string;
	status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
	createdAt: string;
}

const MyBooking: React.FC = () => {
	const { t, i18n } = useTranslation();
	
	const statusMap: Record<string, { label: string; color: string; icon: string }> = {
		pending: { label: t('status.pending', 'Pending'), color: '#f59e0b', icon: '⏳' },
		confirmed: { label: t('status.confirmed', 'Confirmed'), color: '#22c55e', icon: '✅' },
		completed: { label: t('status.completed', 'Completed'), color: '#3b82f6', icon: '🎉' },
		cancelled: { label: t('status.cancelled', 'Cancelled'), color: '#ef4444', icon: '❌' },
	};
	const [searchType, setSearchType] = useState<'email' | 'phone'>('email');
	const [searchValue, setSearchValue] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [bookings, setBookings] = useState<BookingResult[] | null>(null);

	const handleSearch = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!searchValue.trim()) return;

		setLoading(true);
		setError('');
		setBookings(null);

		try {
			const response = await fetch('/api/lookup', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					[searchType]: searchValue.trim(),
				}),
			});

			const result = await response.json();

			if (result.success) {
				setBookings(result.bookings);
				if (result.bookings.length === 0) {
					setError(t('myBooking.noResults', '未找到预约记录'));
				}
			} else {
				setError(result.error || t('myBooking.error', '查询失败，请重试'));
			}
		} catch {
			setError(t('myBooking.error', '查询失败，请重试'));
		}

		setLoading(false);
	};

	const formatDate = (dateStr: string) => {
		return new Date(dateStr + 'T00:00:00').toLocaleDateString(i18n.language, {
			weekday: 'long',
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		});
	};

	return (
		<div className="my-booking-page">
			<SEO title={t('myBooking.title', 'My Booking')} />

			<div className="my-booking-container">
				<div className="my-booking-header">
					<h1>📋 {t('myBooking.title', '查询我的预约')}</h1>
					<p>{t('myBooking.subtitle', '输入您预约时使用的邮箱或手机号查询预约状态')}</p>
				</div>

				{/* 搜索表单 */}
				<div className="search-card">
					<div className="search-tabs">
						<button
							className={`tab ${searchType === 'email' ? 'active' : ''}`}
							onClick={() => setSearchType('email')}
						>
							📧 {t('form.email', '邮箱')}
						</button>
						<button
							className={`tab ${searchType === 'phone' ? 'active' : ''}`}
							onClick={() => setSearchType('phone')}
						>
							📱 {t('form.phone', '手机号')}
						</button>
					</div>

					<form onSubmit={handleSearch} className="search-form">
						<Input
							type={searchType === 'email' ? 'email' : 'tel'}
							value={searchValue}
							onChange={(e) => setSearchValue(e.target.value)}
							placeholder={
								searchType === 'email'
									? t('myBooking.emailPlaceholder', '请输入邮箱地址')
									: t('myBooking.phonePlaceholder', '请输入手机号码')
							}
							required
						/>
						<Button
							type="submit"
							variant="primary"
							loading={loading}
							fullWidth
						>
							{loading ? t('myBooking.searching', '查询中...') : t('myBooking.search', '查询预约')}
						</Button>
					</form>

					{error && <div className="search-error">{error}</div>}
				</div>

				{/* 搜索结果 */}
				{bookings && bookings.length > 0 && (
					<div className="results-section">
						<h2>{t('myBooking.results', '查询结果')} ({bookings.length})</h2>
						<div className="bookings-list">
							{bookings.map((booking) => {
								const status = statusMap[booking.status];
								const isPast = new Date(booking.date) < new Date();

								return (
									<div
										key={booking.id}
										className={`booking-card ${isPast ? 'past' : ''}`}
									>
										<div className="booking-status" style={{ background: status.color }}>
											{status.icon} {status.label}
										</div>

										<div className="booking-details">
											<div className="booking-main">
												<h3>{booking.name}</h3>
												<p className="booking-date">{formatDate(booking.date)}</p>
											</div>

											<div className="booking-info">
												<div className="info-item">
													<span className="label">⏰ {t('form.time', '时间')}</span>
													<span className="value">{booking.time || t('common.toBeConfirmed', 'To be confirmed')}</span>
												</div>
												<div className="info-item">
													<span className="label">👥 {t('form.guests', '人数')}</span>
													<span className="value">{booking.guestCount} {t('form.people', '人')}</span>
												</div>
												<div className="info-item">
													<span className="label">📍 {t('form.region', '地区')}</span>
													<span className="value">{booking.region.toUpperCase()}</span>
												</div>
												<div className="info-item">
													<span className="label">🔖 {t('myBooking.bookingId', '预约号')}</span>
													<span className="value">#{booking.id}</span>
												</div>
											</div>
										</div>

										{booking.status === 'pending' && (
											<div className="booking-note">
												💡 {t('myBooking.pendingNote', '我们将在2小时内联系您确认预约')}
											</div>
										)}

										{booking.status === 'confirmed' && !isPast && (
											<div className="booking-note confirmed">
												🎊 {t('myBooking.confirmedNote', '期待您的到来！厨师将提前30分钟到达')}
											</div>
										)}
									</div>
								);
							})}
						</div>
					</div>
				)}

				{/* 联系信息 */}
				<div className="contact-card">
					<h3>{t('myBooking.needHelp', '需要帮助？')}</h3>
					<p>{t('myBooking.contactUs', '如有任何问题，请随时联系我们')}</p>
					<div className="contact-links">
						<a href={`tel:${CONTACT_INFO.phone}`} className="contact-link phone">
							📞 {CONTACT_INFO.phone}
						</a>
						<a href={`mailto:${CONTACT_INFO.email}`} className="contact-link email">
							📧 {CONTACT_INFO.email}
						</a>
					</div>
				</div>
			</div>
		</div>
	);
};

export default MyBooking;

